import { useMemo } from "react";
import {
  Hammer,
  Wrench,
  Paintbrush,
  Ruler,
  Scissors,
  Drill,
  HardHat,
  Pipette,
  Plug,
  ScanLine,
  Shovel,
  Cog,
  Bolt,
  Flashlight,
  Brush,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

const TOOLS: LucideIcon[] = [
  Hammer, Wrench, Paintbrush, Ruler, Scissors, Drill,
  HardHat, Pipette, Plug, ScanLine, Shovel, Cog,
  Bolt, Flashlight, Brush,
];

interface FloatingTool {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  rotation: number;
  duration: number;
  delay: number;
  dx: number;
  dy: number;
  toolIdx: number;
  opacity: number;
}

export default function AnimatedBackground() {
  const tools = useMemo<FloatingTool[]>(() => {
    const colors = [
      "hsl(215, 65%, 22%)",
      "hsl(215, 65%, 34%)",
      "hsl(24, 75%, 50%)",
      "hsl(215, 55%, 44%)",
      "hsl(24, 65%, 58%)",
      "hsl(215, 45%, 52%)",
    ];

    return Array.from({ length: 32 }, (_, i) => ({
      id: i,
      x: 2 + Math.random() * 96,
      y: 2 + Math.random() * 96,
      size: 30 + Math.random() * 38,
      color: colors[i % colors.length],
      rotation: Math.floor(Math.random() * 360),
      duration: 30 + Math.random() * 35,
      delay: Math.random() * 20,
      dx: (Math.random() - 0.5) * 8,
      dy: (Math.random() - 0.5) * 8,
      toolIdx: i % TOOLS.length,
      opacity: 0.12 + Math.random() * 0.1,
    }));
  }, []);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {tools.map((tool) => {
        const Icon = TOOLS[tool.toolIdx];
        return (
          <div
            key={tool.id}
            className="absolute"
            style={{
              left: `${tool.x}%`,
              top: `${tool.y}%`,
              opacity: tool.opacity,
              animation: `tool-float-${tool.id} ${tool.duration}s ease-in-out ${tool.delay}s infinite, tool-pulse-${tool.id} ${tool.duration * 0.6}s ease-in-out ${tool.delay}s infinite`,
            }}
          >
            <div
              style={{
                transform: `rotate(${tool.rotation}deg)`,
                animation: `tool-spin-${tool.id} ${tool.duration * 3}s linear ${tool.delay}s infinite`,
              }}
            >
              <Icon
                size={tool.size}
                color={tool.color}
                strokeWidth={1.2}
              />
            </div>
          </div>
        );
      })}

      {/* Inject keyframes for each tool */}
      <style>{tools.map((tool) => `
        @keyframes tool-float-${tool.id} {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(${tool.dx}vw, ${tool.dy}vh); }
        }
        @keyframes tool-spin-${tool.id} {
          0% { transform: rotate(${tool.rotation}deg); }
          100% { transform: rotate(${tool.rotation + 360}deg); }
        }
        @keyframes tool-pulse-${tool.id} {
          0%, 100% { opacity: ${tool.opacity * 0.7}; }
          50% { opacity: ${tool.opacity * 1.8}; }
        }
      `).join("")}</style>

      {/* Radial gradient overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,hsl(var(--background))_100%)]" />
    </div>
  );
}
